import type { ResolvedFeed } from '@/types/feeds'
import type { Post } from '@/types/posts'

/**
 * Pure logic for mixing pinned custom feeds into the Following timeline —
 * selection, dedup, and positional sampling. Mirrors
 * mobile/react-native/lib/hooks/mixedTimelineScope.ts (same contract, same
 * tests); a change here needs the same change there.
 *
 * The sampler ports the positional-sampling approach verified from
 * bluesky-social/social-app @ 2d8e349 (2026-10-02) — `src/lib/api/feed/merge.ts`
 * (`MergeFeedAPI`) — rather than a global chronological re-sort: Following's
 * own order is left untouched and a custom-feed post is spliced into fixed
 * slot positions instead. Deliberate deviations from upstream: sources are
 * the user's chooser selection (not all saved feeds/lists), MIN_CANDIDATE_FEEDS
 * is 1 (not 2 — we have a chooser, Bluesky doesn't), feeds are sampled in
 * deterministic pinned order (no shuffle), and there is no feed-count cap —
 * fetch cost is bounded by mixedFeedSupply.ts instead.
 */

/** First N output slots are pure Following — no custom-feed post can land there. */
export const MIX_LEAD_IN = 15

/** Custom-feed posts older than this (relative to `now`) are never sampled. */
export const MIX_MAX_AGE_MS = 24 * 60 * 60 * 1000

/** Minimum number of feeds with posts queued for a slot to be eligible for sampling. */
export const MIN_CANDIDATE_FEEDS = 1

/**
 * Whether 0-indexed output slot `i` is eligible to sample a custom-feed post
 * (still subject to MIN_CANDIDATE_FEEDS and Following availability at call
 * time). Slots 0..MIX_LEAD_IN are always pure Following; from there, roughly
 * 8 of every 20 slots are eligible (i % 4 === 0 || i % 5 === 0).
 */
export function isMixSampleSlot(i: number): boolean {
    return i > MIX_LEAD_IN && (i % 4 === 0 || i % 5 === 0)
}

/**
 * The single shared interpretation of a stored `feeds` selection, used by
 * both the fetch path (useMixedTimeline.ts) and the Feed Settings chooser UI
 * — so they can never compute two different answers for "which feeds are
 * actually mixed in right now."
 *
 * `feeds === null` means "all pinned feeds, in pinned order". An explicit
 * array means the members of `pinnedCustomFeeds` whose uri appears in it,
 * kept in `pinnedCustomFeeds`'s own order (not the stored array's order) —
 * so a stored URI no longer pinned is silently dropped, and reordering pinned
 * feeds doesn't reorder the mix. There is no feed-count cap: how many feeds
 * are fetched at once is bounded by mixedFeedSupply.ts instead.
 */
export function resolveEffectiveMixFeeds(
    pinnedCustomFeeds: ResolvedFeed[],
    feeds: string[] | null
): ResolvedFeed[] {
    if (feeds === null) {
        return pinnedCustomFeeds
    }
    const selected = new Set(feeds)
    return pinnedCustomFeeds.filter(f => selected.has(f.uri))
}

/**
 * Next `feeds` array after toggling one feed's membership in the mix
 * selection from the Feed Settings chooser. If `currentFeeds` is `null` (the
 * user has never customized the selection), it is first materialized into
 * the CURRENTLY EFFECTIVE selection — resolveEffectiveMixFeeds's own
 * all-pinned-feeds interpretation of null — before the toggle is applied.
 * Without this, unchecking one row of an implicit "all pinned" selection
 * would otherwise have nothing to remove a uri from.
 */
export function toggleMixFeedSelection(
    pinnedCustomFeeds: ResolvedFeed[],
    currentFeeds: string[] | null,
    feedUri: string
): string[] {
    const effective = resolveEffectiveMixFeeds(pinnedCustomFeeds, currentFeeds)
    const baseline = currentFeeds ?? effective.map(f => f.uri)
    const isChecked = effective.some(f => f.uri === feedUri)
    return isChecked ? baseline.filter(uri => uri !== feedUri) : [...baseline, feedUri]
}

/**
 * Every key under which `p` can be recognized as "the same post", in priority
 * order: URI, then CID, then author DID + rkey. Dedup has OR semantics — a
 * match on ANY one key is a duplicate — which is why these are bound to
 * canonical groups rather than straight to values (see createIdentityIndex).
 */
export function postIdentityKeys(p: Post): string[] {
    const keys = [`u:${p.uri}`]
    if (p.cid) keys.push(`c:${p.cid}`)
    if (p.author?.did && p.rkey) keys.push(`a:${p.author.did}/${p.rkey}`)
    return keys
}

export interface IdentityIndex<T> {
    /** The record `p` belongs to, or undefined. Read-only: a probe whose keys
     * span two groups returns the earlier one and leaves both unmerged. */
    find(p: Post): { value: T } | undefined
    /** Bind `p`'s keys to a group owning `value`; see createIdentityIndex. */
    claim(p: Post, value: T): { value: T; isNew: boolean }
}

/**
 * Maps post identity keys to canonical connected GROUPS, so "same post" is
 * closed transitively and one physical post can never resolve to two different
 * records depending on which of its keys the caller happens to query.
 *
 * That guarantee holds WITHIN one index. It does NOT compose across two
 * indexes built from different post sets: a probe can hit a group whose owning
 * value shares none of the probe's own keys, because claim() keeps the earliest
 * claimer's value. A caller that resolves a post through one index and then
 * uses the result against another collection has to account for that — see how
 * sampleMixedPostsWithStats resolves a committed row only from posts it has
 * actually held out of the Following tail.
 *
 * Binding keys straight to values would not be transitive: A(uri a, cid X)
 * then B(uri b, cid X) leaves `u:b` → B while `c:X` → A, so B's own URI and
 * CID disagree about which record B is. Instead each group owns one value plus
 * a key set, and the group with the LOWEST id — the earliest writer, i.e. the
 * earliest feed in pinned order, the same precedence that already decides
 * cross-feed attribution — owns any collision: it absorbs the other hit
 * groups and adopts the claiming post's not-yet-bound keys as aliases.
 */
export function createIdentityIndex<T>(): IdentityIndex<T> {
    const keyToGroup = new Map<string, number>()
    const groups: Array<{ value: T; keys: Set<string> }> = []

    /** Ids of every group one of `keys` is bound to, lowest (earliest) first. */
    const hitIds = (keys: string[]): number[] => {
        const ids = new Set<number>()
        for (const key of keys) {
            const id = keyToGroup.get(key)
            if (id !== undefined) ids.add(id)
        }
        return [...ids].sort((a, b) => a - b)
    }

    return {
        find(p) {
            const hit = hitIds(postIdentityKeys(p))[0]
            return hit === undefined ? undefined : { value: groups[hit].value }
        },
        claim(p, value) {
            const keys = postIdentityKeys(p)
            const hits = hitIds(keys)
            if (hits.length === 0) {
                const id = groups.length
                groups.push({ value, keys: new Set(keys) })
                for (const key of keys) keyToGroup.set(key, id)
                return { value, isNew: true }
            }
            const ownerId = hits[0]
            const owner = groups[ownerId]
            // Absorb the other hit groups — `p` proves they are one post. The
            // owner's value is kept; the absorbed groups' values are dropped.
            for (const otherId of hits.slice(1)) {
                for (const key of groups[otherId].keys) {
                    keyToGroup.set(key, ownerId)
                    owner.keys.add(key)
                }
            }
            // Alias `p`'s own unbound keys, so a later post matching only one
            // of them still resolves to this group.
            for (const key of keys) {
                if (!keyToGroup.has(key)) {
                    keyToGroup.set(key, ownerId)
                    owner.keys.add(key)
                }
            }
            return { value: owner.value, isNew: false }
        },
    }
}

/** createIdentityIndex as a membership set — the shape dedup needs. */
export function createIdentitySet() {
    const index = createIdentityIndex<true>()
    return {
        has: (p: Post): boolean => index.find(p) !== undefined,
        /** Also aliases `p`'s extra keys onto whichever group matched it. */
        add: (p: Post): void => {
            index.claim(p, true)
        },
    }
}

/**
 * Which list ROW `p` occupies, as opposed to which post it IS
 * (postIdentityKeys). A feed deliberately renders several rows for one URI —
 * a pinned-slot row and the chronological copy, a repost row and the
 * original — so replay matches Following rows by this and never by URI alone.
 *
 * Mirrors mobile/react-native/lib/postRowKey.ts, which in turn mirrors mobile
 * FeedList.tsx's keyExtractor (the authority for the rule); a change here
 * needs the same change there. `boostedBy` carries no DID on this platform's
 * Post type, so that branch keys on the handle.
 *
 * Note this is deliberately FINER than the webapp's own renderer: PostList
 * keys on `pinnedEntry ? 'pinned-' + uri : uri`, with no repost/boost branch,
 * and nothing on this platform enforces agreement. Finer is the safe
 * direction — an over-specific key makes replay fall back to drift (a fresh
 * arrangement), never falsely retain the wrong row — and `boostedBy` is
 * unreachable in mixed mode anyway, which is Bluesky-only.
 */
export function postRowKey(p: Post): string {
    if (p.pinnedEntry) return `pinned-${p.uri}`
    if (p.repostedBy) return `repost-${p.repostedBy.did ?? p.repostedBy.handle}-${p.uri}`
    if (p.boostedBy) return `boost-${p.boostedBy.handle}-${p.uri}`
    return p.uri
}

export interface MixedFeedSnapshot {
    feed: Pick<ResolvedFeed, 'uri' | 'displayName'>
    posts: Post[]
}

/**
 * An arrangement this sampler already returned and the caller has rendered —
 * the sticky prefix that makes pagination stable (#3063).
 *
 * `injected[i]` marks output row `i` as a mixed-in custom-feed post rather
 * than a Following post. Injection is never inferred from `fromFeedUri`:
 * feedsService stamps that on every custom-feed post regardless of how it
 * reached the list, so field presence is not a reliable marker.
 */
export interface CommittedArrangement {
    posts: readonly Post[]
    injected: readonly boolean[]
}

export interface SampleMixedPostsOptions {
    /** Whether the Following timeline has more pages beyond what's loaded (or is still
     * loading its first page) — output must never run past the loaded Following boundary
     * while this is true; it waits for the next page rather than draining custom posts. */
    followingHasMore: boolean
    now?: number
    /** Rows already on screen. Replayed verbatim (minus evictions) ahead of any
     * newly sampled slot, so a landing supply page can only ever append. */
    committed?: CommittedArrangement
}

/**
 * Splice posts from the selected mix feeds into fixed slot positions within
 * the Following timeline, leaving Following's own order untouched (see
 * module header for why this replaced a chronological merge+sort).
 *
 * Each feed's posts are filtered to MIX_MAX_AGE_MS of `now`, deduplicated by
 * URI, CID, and author DID + rkey (OR semantics — a match on ANY one is a
 * duplicate, not all three — resolved through canonical identity groups, see
 * createIdentityIndex) against Following AND against posts already accepted
 * from an earlier feed, then tagged with their source feed. `snapshots` order
 * is the caller's pinned order — on a cross-feed duplicate, the
 * FIRST-encountered feed wins attribution.
 *
 * Dedup runs against ALL of followingPosts (not just a consumed prefix), so
 * if a later Following page turns out to contain a post already injected,
 * that injected item simply never appears — never a double-show.
 *
 * Pass `committed` (the previous result, fed back) to make the output STICKY,
 * which is what stops pagination jumping (#3063). Committed rows are replayed
 * in their existing order and only slots after them are sampled afresh, so a
 * landing Following page, a rotated-in feed or a late supply page can only
 * append. The exact invariant: *the previous output minus evicted rows is an
 * exact prefix of the new output*. Three things break stickiness on purpose —
 *   - eviction: a committed row whose post is gone from both Following and
 *     every snapshot (deleted, blocked, muted, moderated) is dropped, and the
 *     rows after it move up;
 *   - drift: Following rows reordered or inserted above, or the whole list
 *     replaced, falls back to a fresh stateless arrangement (pull-to-refresh);
 *   - a committed row that later also arrives in a Following page keeps its
 *     original slot, and the Following copy is the one dropped.
 *
 * From slot MIX_LEAD_IN+1 on, roughly 8 of every 20 output slots sample a
 * custom-feed post round-robin (in snapshot/pinned order) instead of the
 * next Following post, as long as at least MIN_CANDIDATE_FEEDS feeds still
 * have posts queued. If Following has nothing ready and no more pages
 * (`!followingHasMore` and the array is exhausted), every remaining slot
 * samples from custom feeds instead — a user with nothing in Following
 * still sees the mixed-in feeds.
 *
 * Returns `followingPosts` unchanged by reference when nothing was injected.
 */
export function sampleMixedPosts(
    followingPosts: Post[],
    snapshots: MixedFeedSnapshot[],
    options: SampleMixedPostsOptions
): Post[] {
    return sampleMixedPostsWithStats(followingPosts, snapshots, options).posts
}

export interface SampleMixedPostsResult {
    posts: Post[]
    /** `remaining[i]` is snapshot `i`'s count of eligible posts still queued after
     * dedup, the age filter and sampling — the "ready" count mixedFeedSupply.ts's
     * round planner uses to decide whether a feed needs a top-up. */
    remaining: number[]
    /** Aligned to `posts`; empty when `posts` is `followingPosts` by reference.
     * Feeds straight back in as the next call's `committed.injected`. */
    injected: boolean[]
}

/**
 * The arrangement a hook has committed to the screen, plus the identity of the
 * session it belongs to. `key` is the account + AppView + sorted feed-membership
 * key; `epoch` is bumped by a refresh.
 */
export interface CommittedRecord {
    key: string
    epoch: number
    posts: readonly Post[]
    injected: readonly boolean[]
}

/**
 * READ side of the committed record — the only thing a hook's render/useMemo
 * may call. Returns the record's content only while it still describes this
 * exact session and generation, so a logout, account or AppView switch,
 * selection change or refresh makes the old prefix simply invisible rather
 * than requiring it to be cleared in time.
 */
export function selectCommitted(
    record: CommittedRecord | null,
    key: string | null,
    epoch: number
): CommittedArrangement | undefined {
    if (!record || key === null) return undefined
    if (record.key !== key || record.epoch !== epoch) return undefined
    return { posts: record.posts, injected: record.injected }
}

export interface PromoteCommittedContext {
    active: boolean
    key: string | null
    epoch: number
    followingPageCount: number
    feedCount: number
    /**
     * Whether this render is one worth making sticky: it actually contains
     * mixed-in rows, and no activation/refresh round is still in flight
     * establishing the arrangement.
     *
     * Both halves matter. An all-Following render needs no sticky prefix — its
     * output IS `followingPosts`, which is already stable — and freezing one
     * would be actively harmful: the arriving mix could then only append BELOW
     * the whole loaded Following list, so it would never reach its designed
     * slots and slot 16 would stay Following for the rest of the session. That
     * is not just the first render of a mount; it is any render where supply
     * has landed but nothing was eligible — a feed whose fetch failed, whose
     * page was entirely past the 24h cutoff, or whose posts all deduped away
     * against Following. "A cache entry exists" is therefore NOT a safe signal.
     */
    supplySettled: boolean
}

/**
 * COMMIT side of the committed record — only ever called from a commit-phase
 * effect, never from render. A render that React discards (a concurrent abort,
 * a superseded transition, a Suspense retry) therefore calls selectCommitted
 * without ever reaching promoteCommitted, which is exactly why posts the user
 * never saw can't be frozen into the prefix.
 *
 * Returns null — no sticky prefix at all — when inactive, when Following is
 * reloading from zero pages, or when nothing is selected to mix.
 */
export function promoteCommitted(
    record: CommittedRecord | null,
    ctx: PromoteCommittedContext,
    rendered: CommittedArrangement
): CommittedRecord | null {
    if (
        !ctx.active ||
        !ctx.supplySettled ||
        ctx.key === null ||
        ctx.followingPageCount === 0 ||
        ctx.feedCount === 0
    ) {
        return null
    }
    // Re-promoting an identical render (a repeated effect under StrictMode) keeps
    // the existing record, so the reference stays stable.
    if (
        record &&
        record.key === ctx.key &&
        record.epoch === ctx.epoch &&
        record.posts === rendered.posts &&
        record.injected === rendered.injected
    ) {
        return record
    }
    return { key: ctx.key, epoch: ctx.epoch, posts: rendered.posts, injected: rendered.injected }
}

/** sampleMixedPosts, plus each snapshot's leftover queue length (see SampleMixedPostsResult). */
export function sampleMixedPostsWithStats(
    followingPosts: Post[],
    snapshots: MixedFeedSnapshot[],
    { followingHasMore, now = Date.now(), committed }: SampleMixedPostsOptions
): SampleMixedPostsResult {
    // Identity indexes are built before the queues on purpose: planning the
    // replay has to be able to REJECT a drifted record, and a rejected record's
    // posts must not have been held out of the queues.
    //
    // Every snapshot post, in pinned order, so the earliest-pinned feed's copy
    // owns a cross-feed duplicate — the same precedence as attribution. Not
    // age-filtered: a committed row past the cutoff still needs a freshness source.
    const snapshotIndex = createIdentityIndex<Post>()
    for (const { posts } of snapshots) {
        for (const post of posts) snapshotIndex.claim(post, post)
    }

    const committedRows = committed?.posts ?? []
    const committedInjectedIndex = createIdentityIndex<Post>()
    for (let i = 0; i < committedRows.length; i++) {
        if (committed?.injected[i]) committedInjectedIndex.claim(committedRows[i], committedRows[i])
    }

    // An already-shown injected row keeps its slot and the later Following copy
    // is the one dropped — evicting a rendered row IS the #3063 bug.
    //
    // The dropped copies get their own index, and replay resolves a committed
    // injected row's fresh fields ONLY from there. An index over ALL of
    // followingPosts would not be safe: claim() keeps the EARLIEST claimer's
    // value, so two Following posts sharing a CID collapse into one group whose
    // value may share no key with the committed row — replay would emit that
    // still-available post as the injected copy and the tail would emit it
    // again, giving two rows one URI and so a duplicate React key.
    const heldOutFollowing = createIdentityIndex<Post>()
    const availableFollowing = followingPosts.filter(p => {
        if (committedInjectedIndex.find(p)) {
            heldOutFollowing.claim(p, p)
            return false
        }
        return true
    })

    const out: Post[] = []
    const injectedOut: boolean[] = []
    let f = 0
    let rr = 0
    let replayed = false
    // The exact post objects replay emitted, so the queue build can hold them
    // out even when a merged identity group means a source shares no key with
    // the committed row that resolved to it.
    const replayedSources = new Set<Post>()

    if (committedRows.length > 0) {
        // Row-key multiset of what Following can still supply, consumed as rows
        // are retained, so "gone" (evict in place) stays distinguishable from
        // "moved" (drift) even when one row key legitimately repeats.
        const availableByRowKey = new Map<string, number>()
        for (const post of availableFollowing) {
            const key = postRowKey(post)
            availableByRowKey.set(key, (availableByRowKey.get(key) ?? 0) + 1)
        }

        const steps: Array<{ post: Post; injected: boolean }> = []
        // Identity groups can merge, so two committed rows can resolve to one
        // source post. Emitting it twice would duplicate a row, so the second
        // occurrence is treated as an eviction.
        const emittedSources = new Set<Post>()
        let cursor = 0
        let replayRr = 0
        let committedFollowing = 0
        let retainedFollowing = 0
        let drifted = false

        for (let i = 0; i < committedRows.length; i++) {
            const entry = committedRows[i]
            if (committed?.injected[i]) {
                // Counts evicted rows too, so the rotation stays deterministic.
                replayRr++
                // Emit-once protects ordering, not content: a row deleted,
                // blocked, muted or taken down since it was shown is evicted.
                const source =
                    heldOutFollowing.find(entry)?.value ?? snapshotIndex.find(entry)?.value
                if (!source || emittedSources.has(source)) continue
                emittedSources.add(source)
                // Ordinary fields come from the freshest copy; the committed
                // provenance and position are always preserved.
                const sameProvenance =
                    source.fromFeedUri === entry.fromFeedUri &&
                    source.fromFeedName === entry.fromFeedName
                steps.push({
                    post: sameProvenance
                        ? source
                        : {
                              ...source,
                              fromFeedUri: entry.fromFeedUri,
                              fromFeedName: entry.fromFeedName,
                          },
                    injected: true,
                })
                continue
            }
            committedFollowing++
            const key = postRowKey(entry)
            const current = availableFollowing[cursor]
            if (current && postRowKey(current) === key) {
                steps.push({ post: current, injected: false })
                cursor++
                retainedFollowing++
                availableByRowKey.set(key, (availableByRowKey.get(key) ?? 1) - 1)
                continue
            }
            if ((availableByRowKey.get(key) ?? 0) === 0) {
                // Gone from Following altogether (removePost, moderation, block,
                // mute, takedown): evict in place — later rows move up and the
                // Following cursor deliberately does not advance.
                continue
            }
            // Still present, but not here: Following was reordered or had rows
            // inserted above. A fresh arrangement is the right outcome.
            drifted = true
            break
        }

        // Retaining none of the committed Following rows although there were
        // some has two causes, and the rows themselves cannot tell them apart:
        // the whole list was replaced (a refresh), or every one of them was
        // individually evicted (one blocked/muted/deleted author who wrote all
        // of them — no refresh involved, so no epoch bump drops the record for
        // us). What separates them is whether anything is actually standing in
        // their place. A committed record always covers every Following post
        // loaded when it was made — the sampling loop below only stops once
        // Following is exhausted — and reaching here without drift means no
        // committed row survives anywhere in `availableFollowing`. So anything
        // still in `availableFollowing` is Following content that arrived after
        // the record was committed, i.e. something really did take those slots;
        // an empty one means the rows merely went away. Treating mass eviction
        // as a replacement would discard still-valid injected rows the user has
        // already scrolled past, which is #3063 itself.
        const wholeListReplaced =
            committedFollowing > 0 && retainedFollowing === 0 && availableFollowing.length > 0
        if (!drifted && !wholeListReplaced) {
            for (const step of steps) {
                out.push(step.post)
                injectedOut.push(step.injected)
            }
            f = cursor
            rr = replayRr
            replayed = true
            for (const source of emittedSources) replayedSources.add(source)
        }
    }

    // On drift the record is ignored in full, including its Decision-3 drops.
    const following = replayed ? availableFollowing : followingPosts

    const seen = createIdentitySet()
    for (const post of followingPosts) seen.add(post)
    if (replayed) {
        // A replayed injected row is held out of every queue: it is already on
        // screen, so it must neither repeat nor count as "ready" supply.
        for (let i = 0; i < committedRows.length; i++) {
            if (committed?.injected[i]) seen.add(committedRows[i])
        }
        // Belt and braces for the merged-group case above: a snapshot copy that
        // replay emitted may share no key with the committed row, so holding out
        // the committed rows alone would let it into a queue and be shown twice.
        for (const source of replayedSources) seen.add(source)
    }

    const queues: Post[][] = snapshots.map(({ feed, posts }) => {
        const queue: Post[] = []
        for (const post of posts) {
            const createdAtMs = new Date(post.createdAt).getTime()
            if (!Number.isFinite(createdAtMs) || now - createdAtMs > MIX_MAX_AGE_MS) continue
            if (seen.has(post)) {
                // Claimed even though it's rejected, so this copy's extra keys
                // alias onto the group that matched it — a later post sharing
                // only one of those keys is then recognized as the same post.
                seen.add(post)
                continue
            }

            seen.add(post)
            queue.push({ ...post, fromFeedUri: feed.uri, fromFeedName: feed.displayName })
        }
        return queue
    })

    let injectedAny = injectedOut.includes(true)
    for (let i = out.length; ; i++) {
        const candidates = queues.filter(q => q.length > 0)
        const hasFollowReady = f < following.length
        const hasFollows = hasFollowReady || followingHasMore
        if (candidates.length === 0 && !hasFollows) break

        if ((isMixSampleSlot(i) && candidates.length >= MIN_CANDIDATE_FEEDS) || !hasFollows) {
            const queue = candidates[rr % candidates.length]
            rr++
            out.push(queue.shift()!)
            injectedOut.push(true)
            injectedAny = true
            continue
        }
        if (!hasFollowReady) break
        out.push(following[f++])
        injectedOut.push(false)
    }

    // Dropping a Following duplicate of a committed row also makes `out` differ
    // from `followingPosts`, so it can't be returned by reference either.
    const changed = injectedAny || following.length !== followingPosts.length
    return {
        posts: changed ? out : followingPosts,
        remaining: queues.map(q => q.length),
        injected: changed ? injectedOut : [],
    }
}
